import type { configMode, ExportTypes, ReportConfig } from "types/shim";

export const colorMap = {
  blue: [0, 0, 128],
  red: [128, 0, 0],
  lightGray: [235, 235, 235],
  darkGray: [50, 50, 50],
};

export const brandMap = {
  brand: "TaxEcoBit",
  brandLogo: "logos/tax_logo.png",
  brandReportDesc: "TaxEcoBit CBAM Report",
};

// 报告字典
export const reportDict = {
  "zh-cn": {
    titleLabel: "标题",
    title: "CBAM 排放报告",
    complianceLabel: "合规声明",
    compliance: "合规声明: 本报告符合欧盟CBAM法规要求。",
    emissionDetails: "排放详情",
    summary: "总结",
    summaryOfEmissions: "排放总结",
    company: "公司",
    reportId: "报告 ID",
    date: "报告日期",
    contact: "联系人",
    email: "邮箱",
    origin: "起始地",
    destination: "目的地",
    mode: "运输方式",
    distance: "距离",
    weight: "重量",
    cn_code: "CN 代码",
    transportEmission: "运输排放",
    productionEmission: "生产排放",
    transportFactor: "运输因子(g CO2e/t-km)",
    productionFactor: "生产因子(kg CO2e/kg)",
    totalDistance: "总距离",
    totalEmission: "总排放",
    tax: "税额",
    page: "第 $page 页 / 共 $total 页",
    audit: "生成时间",
    inviteText: "扫码加入TaxEcoBit社区",
    watermarkFree: "试用版 - 升级去除水印",
    watermarkPro: "由 TaxEcoBit 生成",
    disclaimerFree: [
      "* 本报告为免费预览版，建议升级 Pro 获取完整功能。",
      "Pro 版支持自定义 Logo、Excel 导出、无水印。",
    ],
    disclaimerPro: [
      "* 本报告由 TaxEcoBit 生成，精度 > 90%。",
      "如需正式提交，请参考欧盟 CBAM 官方指南。",
    ],
    disclaimerFreeLabel: "免费版报告",
    disclaimerProLabel: "专业版报告",
    signature: "用户签名",
    generatedBy: "生成由",
    emissionBreakdown: "排放分解",
    emissionsLabel: "排放量(kg CO2e)",
    logo: "标志",
    info: "信息",
    signatureStatus: "已签名",
    creator: "创建者",
    description: "描述",
    footer: "页脚",
    hash: "ハッシュ値",
    signatureFail: "签名失败",
    mode_sea: "海运",
    mode_road: "公路",
    mode_rail: "铁路",
    mode_barge: "驳船",
    leg: "分段 $index ($mode)",
    emissionFactors: "排放因子",
    basedOn: "基于",
  },
  en: {
    titleLabel: "Title",
    title: "CBAM Emission Report",
    complianceLabel: "Compliance Statement",
    compliance: "Compliance Statement: This report complies with EU CBAM regulations.",
    emissionDetails: "Emission Details",
    summary: "Summary",
    summaryOfEmissions: "Summary of Emissions",
    company: "Company",
    reportId: "Report ID",
    date: "Report Date",
    contact: "Contact",
    email: "Email",
    origin: "Origin",
    destination: "Destination",
    mode: "Mode",
    distance: "Distance",
    weight: "Weight",
    cn_code: "CN Code",
    transportEmission: "Transport Emission",
    productionEmission: "Production Emission",
    transportFactor: "Transport Factor(g CO2e/t-km)",
    productionFactor: "Production Factor(kg CO2e/kg)",
    totalDistance: "Total Distance",
    totalEmission: "Total Emission",
    tax: "Tax",
    page: "Page $page of $total",
    audit: "Generated Time",
    inviteText: "Join the TaxEcoBit Community",
    watermarkFree: "Trial Version - Generated by TaxEcoBit",
    watermarkPro: "Generated by TaxEcoBit",
    disclaimerFree: [
      "* This is a free preview version of the report. Upgrade to Pro for full functionality.",
      "Pro version supports custom logo, Excel export, and watermark removal.",
    ],
    disclaimerPro: [
      "* This report is generated by TaxEcoBit with >90% accuracy.",
      "For official submission, please refer to the EU CBAM guidelines.",
    ],
    disclaimerFreeLabel: "Free version report",
    disclaimerProLabel: "Professional version report",
    signature: "User Signature",
    generatedBy: "Generated by",
    emissionBreakdown: "Emission Breakdown",
    emissionsLabel: "Emissions(kg CO2e)",
    logo: "Logo",
    info: "Info",
    signatureStatus: "Signed",
    creator: "Creator",
    description: "Description",
    footer: "Footer",
    hash: "Hash Value",
    signatureFail: "Signature Failed",
    mode_sea: "Sea",
    mode_road: "Road",
    mode_rail: "Rail",
    mode_barge: "Barge",
    leg: "Leg $index ($mode)",
    emissionFactors: "Emission Factors",
    basedOn: "Based On",
  },
  de: {
    titleLabel: "Titel",
    title: "CBAM-Emissionsbericht",
    complianceLabel: "Konformitätserklärung",
    compliance: "Konformitätserklärung: Dieser Bericht entspricht den EU-CBAM-Vorschriften.",
    emissionDetails: "Emissionsdetails",
    summary: "Zusammenfassung",
    summaryOfEmissions: "Zusammenfassung der Emissionen",
    company: "Unternehmen",
    reportId: "Bericht-ID",
    date: "Berichtsdatum",
    contact: "Kontakt",
    email: "E-Mail",
    origin: "Startort",
    destination: "Zielort",
    mode: "Transportmittel",
    distance: "Entfernung",
    weight: "Gewicht",
    cn_code: "CN-Code",
    transportEmission: "Transportemissionen",
    productionEmission: "Produktionsemissionen",
    transportFactor: "Transportfaktor(g CO2e/t-km)",
    productionFactor: "Produktionsfaktor(kg CO2e/kg)",
    totalDistance: "Gesamtentfernung",
    totalEmission: "Gesamtemissionen",
    tax: "Steuer",
    page: "Seite $page von $total",
    audit: "Erstellungszeit",
    inviteText: "Treten Sie der TaxEcoBit-Community bei",
    watermarkFree: "Testversion - Erstellt von TaxEcoBit",
    watermarkPro: "Erstellt von TaxEcoBit",
    disclaimerFree: [
      "* Dies ist eine kostenlose Vorschauversion des Berichts. Führen Sie ein Upgrade auf Pro durch, um die volle Funktionalität zu erhalten.",
      "Die Pro-Version unterstützt benutzerdefinierte Logos, Excel-Export und das Entfernen von Wasserzeichen.",
    ],
    disclaimerPro: [
      "* Dieser Bericht wird von TaxEcoBit mit einer Genauigkeit von >90% generiert.",
      "Für eine offizielle Einreichung beachten Sie bitte die EU-CBAM-Richtlinien.",
    ],
    disclaimerFreeLabel: "Kostenlose Version Bericht",
    disclaimerProLabel: "Professionelle Version Bericht",
    signature: "Benutzerunterschrift",
    generatedBy: "Erstellt von",
    emissionBreakdown: "Emissionsaufschlüsselung",
    emissionsLabel: "Emissionen(kg CO2e)",
    logo: "Logo",
    info: "Info",
    signatureStatus: "Unterschrieben",
    creator: "Ersteller",
    description: "Beschreibung",
    footer: "Fußzeile",
    hash: "Hashwert",
    signatureFail: "Unterschrift fehlgeschlagen",
    mode_sea: "Seeschifffahrt",
    mode_road: "Straße",
    mode_rail: "Bahn",
    mode_barge: "Binnenschiff",
    leg: "Abschnitt $index ($mode)",
    emissionFactors: "Emissionsfaktoren",
    basedOn: "Basé sur",
  },
  fr: {
    titleLabel: "Titre",
    title: "Rapport d’émissions CBAM",
    complianceLabel: "Déclaration de conformité",
    compliance:
      "Déclaration de conformité : Ce rapport est conforme aux réglementations CBAM de l’UE.",
    emissionDetails: "Détails des émissions",
    summary: "Résumé",
    summaryOfEmissions: "Résumé des émissions",
    company: "Entreprise",
    reportId: "ID du rapport",
    date: "Date du rapport",
    contact: "Contact",
    email: "E-mail",
    origin: "Origine",
    destination: "Destination",
    mode: "Mode de transport",
    distance: "Distance",
    weight: "Poids",
    cn_code: "Code CN",
    transportEmission: "Émissions de transport",
    productionEmission: "Émissions de production",
    transportFactor: "Facteur de transport(g CO2e/t-km)",
    productionFactor: "Facteur de production(kg CO2e/kg)",
    totalDistance: "Distance totale",
    totalEmission: "Émissions totales",
    tax: "Taxe",
    page: "Page $page sur $total",
    audit: "Heure de génération",
    inviteText: "Rejoignez la communauté TaxEcoBit",
    watermarkFree: "Version d'essai - Généré par TaxEcoBit",
    watermarkPro: "Généré par TaxEcoBit",
    disclaimerFree: [
      "* Il s'agit d'une version d'aperçu gratuite du rapport. Passez à la version Pro pour bénéficier de toutes les fonctionnalités.",
      "La version Pro prend en charge le logo personnalisé, l'exportation Excel et la suppression des filigranes.",
    ],
    disclaimerPro: [
      "* Ce rapport est généré par TaxEcoBit avec une précision >90%.",
      "Pour une soumission officielle, veuillez vous référer aux directives de l'UE CBAM.",
    ],
    disclaimerFreeLabel: "Rapport version gratuite",
    disclaimerProLabel: "Rapport version professionnelle",
    signature: "Signature de l’utilisateur",
    generatedBy: "Généré par",
    emissionBreakdown: "Répartition des émissions",
    emissionsLabel: "Émissions(kg CO2e)",
    logo: "Logo",
    info: "Info",
    signatureStatus: "Signé",
    creator: "Créateur",
    description: "Description",
    footer: "Pied de page",
    hash: "Valeur de hachage",
    signatureFail: "Échec de la signature",
    mode_sea: "Maritime",
    mode_road: "Routier",
    mode_rail: "Ferroviaire",
    mode_barge: "Péniche",
    leg: "Segment $index ($mode)",
    emissionFactors: "Facteurs d’émission",
    basedOn: "Basé sur",
  },
  ja: {
    titleLabel: "タイトル",
    title: "CBAM 排出報告",
    complianceLabel: "コンプライアンス声明",
    compliance: "コンプライアンス声明: このレポートはEU CBAM規制に準拠しています。",
    emissionDetails: "排出詳細",
    summary: "概要",
    summaryOfEmissions: "排出概要",
    company: "会社",
    reportId: "レポートID",
    date: "報告日",
    contact: "連絡先",
    email: "メール",
    origin: "出発地",
    destination: "目的地",
    mode: "輸送手段",
    distance: "距離",
    weight: "重量",
    cn_code: "CNコード",
    transportEmission: "輸送排出量",
    productionEmission: "生産排出量",
    transportFactor: "輸送因子(g CO2e/t-km)",
    productionFactor: "生産因子(kg CO2e/kg)",
    totalDistance: "総距離",
    totalEmission: "総排出量",
    tax: "税金",
    page: "ページ $page / $total",
    audit: "生成時間",
    inviteText: "TaxEcoBitコミュニティに参加",
    watermarkFree: "トライアル版 - TaxEcoBit によって生成",
    watermarkPro: "TaxEcoBit によって生成",
    disclaimerFree: [
      "* これは無料のプレビューバージョンのレポートです。フル機能を利用するにはProにアップグレードしてください。",
      "Pro版はカスタムロゴ、Excelエクスポート、透かしの削除をサポートしています。",
    ],
    disclaimerPro: [
      "* このレポートはTaxEcoBitによって生成され、精度は90%以上です。",
      "正式提出の際は、EU CBAMガイドラインを参照してください。",
    ],
    disclaimerFreeLabel: "無料版レポート",
    disclaimerProLabel: "プロ版レポート",
    signature: "署名",
    generatedBy: "生成元",
    emissionBreakdown: "排出分解",
    emissionsLabel: "排出量(kg CO2e)",
    logo: "ロゴ",
    info: "情報",
    signatureStatus: "署名済み",
    creator: "作成者",
    description: "説明",
    footer: "フッター",
    hash: "ハッシュ値",
    signatureFail: "署名に失敗しました",
    mode_sea: "海運",
    mode_road: "道路",
    mode_rail: "鉄道",
    mode_barge: "はしけ",
    leg: "区間 $index ($mode)",
    emissionFactors: "排出係数",
    basedOn: "基準",
  },
  "zh-tw": {
    titleLabel: "標題",
    title: "CBAM 排放報告",
    complianceLabel: "合規聲明",
    compliance: "合規聲明: 本報告符合歐盟CBAM法規要求。",
    emissionDetails: "排放詳情",
    summary: "總結",
    summaryOfEmissions: "排放總結",
    company: "公司",
    reportId: "報告 ID",
    date: "報告日期",
    contact: "聯絡人",
    email: "郵箱",
    origin: "起始地",
    destination: "目的地",
    mode: "運輸方式",
    distance: "距離",
    weight: "重量",
    cn_code: "CN 代碼",
    transportEmission: "運輸排放",
    productionEmission: "生產排放",
    transportFactor: "運輸因子(g CO2e/t-km)",
    productionFactor: "生產因子(kg CO2e/kg)",
    totalDistance: "總距離",
    totalEmission: "總排放",
    tax: "稅額",
    page: "第 $page 頁 / 共 $total 頁",
    audit: "生成時間",
    inviteText: "掃碼加入TaxEcoBit社區",
    watermarkFree: "試用版 - 由 TaxEcoBit 生成",
    watermarkPro: "由 TaxEcoBit 生成",
    disclaimerFree: [
      "* 本報告為免費預覽版，建議升級 Pro 獲取完整功能。",
      "Pro 版支援自訂 Logo、Excel 匯出、無浮水印。",
    ],
    disclaimerPro: [
      "* 本報告由 TaxEcoBit 生成，精度 > 90%。",
      "如需正式提交，請參考歐盟 CBAM 官方指南。",
    ],
    disclaimerFreeLabel: "免費版報告",
    disclaimerProLabel: "專業版報告",
    signature: "用戶簽名",
    generatedBy: "生成由",
    emissionBreakdown: "排放分解",
    emissionsLabel: "排放量(kg CO2e)",
    logo: "標誌",
    info: "信息",
    signatureStatus: "已簽名",
    creator: "創建者",
    description: "描述",
    footer: "頁腳",
    hash: "ハッシュ값",
    signatureFail: "簽名失敗",
    mode_sea: "海運",
    mode_road: "公路",
    mode_rail: "鐵路",
    mode_barge: "駁船",
    leg: "分段 $index ($mode)",
    emissionFactors: "排放因子",
    basedOn: "基於",
  },
  es: {
    titleLabel: "Título",
    title: "Informe de Emisiones CBAM",
    complianceLabel: "Declaración de Cumplimiento",
    compliance:
      "Declaración de Cumplimiento: Este informe cumple con las regulaciones del CBAM de la UE.",
    emissionDetails: "Detalles de Emisiones",
    summary: "Resumen",
    summaryOfEmissions: "Resumen de Emisiones",
    company: "Empresa",
    reportId: "ID del Informe",
    date: "Fecha del Informe",
    contact: "Contacto",
    email: "Correo Electrónico",
    origin: "Origen",
    destination: "Destino",
    mode: "Modo de Transporte",
    distance: "Distancia",
    weight: "Peso",
    cn_code: "Código NC",
    transportEmission: "Emisión de Transporte",
    productionEmission: "Emisión de Producción",
    transportFactor: "Factor de Transporte(g CO2e/t-km)",
    productionFactor: "Factor de Producción(kg CO2e/kg)",
    totalDistance: "Distancia Total",
    totalEmission: "Emisión Total",
    tax: "Impuesto",
    page: "Página $page de $total",
    audit: "Hora de Generación",
    inviteText: "Únete a la Comunidad TaxEcoBit",
    watermarkFree: "Versión de prueba - Generado por TaxEcoBit",
    watermarkPro: "Generado por TaxEcoBit",
    disclaimerFree: [
      "* Esta es una versión de vista previa gratuita del informe. Actualice a Pro para obtener todas las funciones.",
      "La versión Pro admite logotipo personalizado, exportación a Excel y eliminación de marcas de agua.",
    ],
    disclaimerPro: [
      "* Este informe es generado por TaxEcoBit con una precisión >90%.",
      "Para presentaciones oficiales, consulte las directrices de la UE CBAM.",
    ],
    disclaimerFreeLabel: "Informe versión gratuita",
    disclaimerProLabel: "Informe versión profesional",
    signature: "Firma del Usuario",
    generatedBy: "Generado por",
    emissionBreakdown: "Desglose de Emisiones",
    emissionsLabel: "Emisiones(kg CO2e)",
    logo: "Logo",
    info: "Información",
    signatureStatus: "Firmado",
    creator: "Creador",
    description: "Descripción",
    footer: "Pie de Página",
    hash: "Valor Hash",
    signatureFail: "Firma Fallida",
    mode_sea: "Marítimo",
    mode_road: "Carretera",
    mode_rail: "Ferrocarril",
    mode_barge: "Barcaza",
    leg: "Tramo $index ($mode)",
    emissionFactors: "Factores de Emisión",
    basedOn: "Basado en",
  },
};

// 默认配置
export const presetsConfig: Record<string, ReportConfig> = {
  default: {
    export_types: ["pdf"],
    config_mode: "default",
    common: {
      fontSize: { title: 16, text: 12, tableHeader: 12, tableCell: 10, watermark: 24 },
      color: { title: "blue", text: [0, 0, 0], tableHeader: "lightGray" },
      language: "en",
      // abnormalThreshold: 10,
      dataFormat: "1,000.00",
      showFields: {
        logo: false,
        company: false,
        date: true,
        contact: true,
        email: true,
        emissionDetails: true,
        summary: true,
        totalDistance: true,
        productionEmission: true,
        transportEmission: true,
        totalEmission: true,
        tax: true,
        compliance: true,
        title: true,
      },
      footerNote: "TaxEcoBit CBAM Compliance Report",
      currency: "EUR",
    },
    pdf: {
      tableStyle: { borderStyle: "none" },
      logoPosition: "top-right",
      removeWatermark: false,
      customWatermark: "",
    },
    excel: {
      columnWidth: 100,
      cellColor: "#FFFFFF",
      mergeHeader: false,
      borderStyle: "none",
      enableFilter: false,
      addSignature: false,
      signatureContent: "",
    },
    privacyHiddenFields: {
      companyName: true,
      contact: true,
      email: true,
      logo: true,
      weight: true,
    },
  },
  professional: {
    export_types: ["pdf"],
    config_mode: "professional",
    common: {
      fontSize: { title: 18, text: 12, tableHeader: 12, tableCell: 10, watermark: 28 },
      color: { title: "blue", text: [0, 0, 0], tableHeader: "darkGray" },
      language: "en",
      // abnormalThreshold: 15,
      dataFormat: "1,000.00",
      showFields: {
        logo: false,
        company: false,
        date: true,
        contact: true,
        email: true,
        emissionDetails: true,
        summary: true,
        totalDistance: true,
        productionEmission: true,
        transportEmission: true,
        totalEmission: true,
        tax: true,
        compliance: true,
        title: true,
      },
      footerNote: "TaxEcoBit CBAM Compliance Report",
      currency: "EUR",
    },
    pdf: {
      tableStyle: { borderStyle: "none" },
      logoPosition: "top-right",
      removeWatermark: false,
      customWatermark: "",
    },
    excel: {
      columnWidth: 120,
      cellColor: "#F0F0F0",
      mergeHeader: false,
      borderStyle: "none",
      enableFilter: false,
      addSignature: false,
      signatureContent: "",
    },
    privacyHiddenFields: {
      companyName: true,
      contact: true,
      email: true,
      logo: true,
      weight: true,
    },
  },
  simple: {
    export_types: ["pdf"] as ExportTypes[],
    config_mode: "simple" as configMode,
    common: {
      fontSize: { title: 14, text: 10, tableHeader: 10, tableCell: 10, watermark: 20 },
      color: { title: "blue", text: [0, 0, 0], tableHeader: "lightGray" },
      language: "en",
      // abnormalThreshold: 5,
      dataFormat: "1,000.00",
      showFields: {
        logo: false,
        company: false,
        date: true,
        contact: false,
        email: false,
        emissionDetails: true,
        summary: true,
        totalDistance: true,
        productionEmission: false,
        transportEmission: false,
        totalEmission: true,
        tax: true,
        compliance: false,
        title: true,
      },
      footerNote: "TaxEcoBit CBAM Compliance Report",
      currency: "EUR",
    },
    pdf: {
      tableStyle: { borderStyle: "none" },
      logoPosition: "top-right",
      removeWatermark: false,
      customWatermark: "",
    },
    excel: {
      columnWidth: 80,
      cellColor: "#FFFFFF",
      mergeHeader: false,
      borderStyle: "none",
      enableFilter: false,
      addSignature: false,
      signatureContent: "",
    },
    privacyHiddenFields: {
      companyName: true,
      contact: true,
      email: true,
      logo: true,
      weight: true,
    },
  },
};

export const currencyFormats = {
  EUR: "€",
  USD: "$",
  GBP: "£",
};

export const premiumFields = [
  "common.language",
  // 'common.abnormalThreshold',
  "common.showChart",
  "common.batchExport",
  "common.showFields.logo",
  "common.showFields.company",
  "common.companyName",
  "common.customContact",
  "common.customEmail",
  "common.customCompliance",
  "pdf.removeWatermark",
  "pdf.showSignature",
  "pdf.removeInviteQR",
  "pdf.logoPosition",
  "pdf.tableStyle.borderStyle",
  "common.color.premiumTitle",
  "common.color.customTableHeader",
  "common.footerNote",
  "pdf.customWatermark",
  "pdf.inviteText",
  "excel.columnWidth",
  "excel.cellColor",
  "excel.mergeHeader",
  "excel.borderStyle",
  "excel.enableFilter",
  "excel.addSignature",
  "excel.signatureContent",
];

/**
 * API配額常量定义
 */

// API密钥数量限制
export const API_KEY_LIMITS = {
  Free: 0,
  Trial: 1,
  Basic: 1,
  Pro: 5,
} as const;

// 计划限制（包括计算次数、报告数量等）
export const PLAN_LIMITS = {
  Free: {
    count: 0,
    dailyLimit: null,
    total: 0,
    calculations: 100,
    reports: 30,
    dailyReport: 1,
    monthlyPrice: 0,
    annualPrice: 0,
  },
  Trial: {
    count: 1,
    dailyLimit: 50,
    total: 700,
    calculations: 500,
    reports: 150,
    dailyReport: 5,
    monthlyPrice: 0,
    annualPrice: 0,
    trialLastDays: 14,
  },
  Basic: {
    count: 1,
    dailyLimit: null,
    total: 500,
    calculations: 500,
    reports: 50,
    dailyReport: null,
    monthlyPrice: 49,
    annualPrice: 499,
  },
  Pro: {
    count: 5,
    dailyLimit: null,
    total: 5000,
    calculations: 5000,
    reports: 500,
    dailyReport: null,
    monthlyPrice: 99,
    annualPrice: 999,
  },
} as const;

export type PlanType = keyof typeof API_KEY_LIMITS;
